/**
 * MonthlyWorkSpine — แถบ "งานเดือนนี้" บนสุดของแดชบอร์ด owner (Non-HR UX §2).
 *
 * แก้ feedback: เปิดแอปมาไม่เห็นภาพรวม/ลำดับงาน. โชว์วงจรทำเงินเดือนทั้งเดือน
 * เป็น 4 สเต็ป (สรุปเวลา → ตรวจก่อนปิด → ทำเงินเดือน → ส่งสลิป) + บอกว่าอยู่ขั้นไหน
 * + "ต้องทำอะไรต่อ" เป็นการ์ดเดียว (NextActionCard). กดสเต็ปไหนก็กระโดดไปได้.
 *
 * สถานะ derive จาก: payroll run status ของงวด + close-readiness (ค้าง/พร้อม).
 * เป็นไกด์ภาพรวม (coarse) — ไม่ใช่ state machine เป๊ะ.
 */
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { CheckCircle2, ClipboardCheck, Banknote, Send, ListChecks } from 'lucide-react';
import type { PayrollStatus } from '@payroll/shared';
import { apiFetch } from '../lib/api-client';
import { useCloseReadiness } from '../hooks';
import { Stepper } from './ui/GuidedFlow';
import { NextActionCard } from './ui/NextActionCard';
import { currentPeriod } from '../lib/thai-date';

const STAGES = [
  { id: 'reconcile', label: 'สรุปเวลา', hash: '#reconcile' },
  { id: 'close', label: 'ตรวจก่อนปิด', hash: '#close' },
  { id: 'payroll', label: 'ทำเงินเดือน', hash: '#payroll' },
  { id: 'slips', label: 'ส่งสลิป', hash: '#payroll' },
];

const SENT_STATES: PayrollStatus[] = ['sent'];
const SENDING_STATES: PayrollStatus[] = ['locked', 'pdf_ready', 'sending', 'partial_sent'];
const RUN_STATES: PayrollStatus[] = ['previewed', 'approved'];

export function MonthlyWorkSpine() {
  const navigate = useNavigate();
  const period = currentPeriod();
  const periodLabel = new Date(`${period}-01`).toLocaleDateString('th-TH', { month: 'long', year: 'numeric' });

  const { data: rows } = useQuery({
    queryKey: ['payroll-run-status', period],
    queryFn: () => apiFetch<Array<{ status: PayrollStatus }>>(`/owner/payroll/run?period=${period}`),
    staleTime: 60_000,
  });
  const { data: readiness } = useCloseReadiness(period);
  const status = rows?.[0]?.status ?? null;

  // derive ขั้นปัจจุบัน + งานถัดไป
  let current = 2;
  let done = false;
  let action: { icon: typeof Banknote; tone?: 'warn'; title: string; detail?: string; cta: string; hash: string } | null = null;

  if (status && SENT_STATES.includes(status)) {
    current = 3;
    done = true;
  } else if (status && SENDING_STATES.includes(status)) {
    current = 3;
    action = { icon: Send, title: 'ส่งสลิปให้พนักงาน', detail: 'เงินเดือนล็อกแล้ว — ส่งสลิปทาง LINE ได้เลย', cta: 'ส่งสลิป', hash: '#payroll' };
  } else if (status && RUN_STATES.includes(status)) {
    current = 2;
    action = status === 'approved'
      ? { icon: Banknote, title: 'ล็อกงวดเงินเดือน', detail: 'อนุมัติแล้ว — ล็อกเพื่อใช้เป็นยอดจ่ายจริง', cta: 'ไปล็อกงวด', hash: '#payroll' }
      : { icon: Banknote, title: 'อนุมัติเงินเดือนงวดนี้', detail: 'ตรวจยอดแล้วส่งอนุมัติ', cta: 'ไปอนุมัติ', hash: '#payroll' };
  } else if (readiness && !readiness.ready) {
    current = 1;
    action = readiness.hardBlockCount > 0
      ? { icon: ClipboardCheck, tone: 'warn', title: 'ปิดงวดไม่ได้ — ต้องแก้ก่อน', detail: `มี ${readiness.hardBlockCount} รายการต้องแก้ (เช่น ลืมทำเงินเดือน / ยังไม่ตั้งฐานเงินเดือน)`, cta: 'ไปแก้', hash: '#close' }
      : { icon: ClipboardCheck, tone: 'warn', title: 'ตรวจก่อนปิดงวด', detail: `มี ${readiness.affectsCycleCount} เรื่องควรเคลียร์ก่อนทำเงินเดือน`, cta: 'ไปตรวจ', hash: '#close' };
  } else {
    current = 2;
    action = { icon: Banknote, title: 'ทำเงินเดือนงวดนี้', detail: 'พร้อมแล้ว — เริ่มคำนวณร่างเงินเดือนได้', cta: 'ทำเงินเดือน', hash: '#payroll' };
  }

  return (
    <div className="card p-4 mb-6">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <ListChecks className="w-4 h-4 text-brand-600" />
          <span className="font-semibold">งานเดือนนี้ · {periodLabel}</span>
        </div>
        <Link to="/owner/cycle" className="text-xs text-brand-700 hover:underline shrink-0">เปิดรอบเงินเดือน →</Link>
      </div>

      <Stepper steps={STAGES} current={current} onStepClick={(i) => navigate('/owner/cycle' + STAGES[i]!.hash)} />

      <div className="mt-4">
        {done ? (
          <div className="rounded-xl border border-brand-200 bg-brand-50 p-4 flex items-center gap-3">
            <CheckCircle2 className="w-6 h-6 text-brand-600 shrink-0" />
            <div>
              <div className="font-medium text-brand-900">งวดนี้เสร็จแล้ว</div>
              <div className="text-sm text-brand-800">ส่งสลิปครบ — รอบหน้าค่อยเริ่มใหม่</div>
            </div>
          </div>
        ) : action ? (
          <NextActionCard
            icon={action.icon}
            tone={action.tone ?? 'default'}
            title={action.title}
            detail={action.detail}
            action={{ label: action.cta, to: '/owner/cycle' + action.hash }}
          />
        ) : null}
      </div>
    </div>
  );
}
